import React, { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { getInitials } from 'src/utils/get-initials';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Label from 'src/components/Label';
import { permissions } from 'src/paths';
import { useSelector } from 'react-redux';

interface PatientListTableProps {
  count?: number;
  items?: any;
  onPageChange?: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (event: ChangeEvent<HTMLInputElement>) => void;
  page?: number;
  rowsPerPage?: number;
}

const ListingTable: FC<PatientListTableProps> = (props) => {
  const { items = [], onRowClick = () => {} } = props;

  const formatSSN = (ssn: string) => {
    const formattedSSN = ssn.replace(/(\d{3})(\d{2})(\d{4})/, '$1-$2-$3');
    return formattedSSN;
  };

  const { CREATE_PATIENT, READ_EPISODE_OF_CARE } = permissions;
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const getAllPatientRes = useSelector((state: any) => state.getAllPatientListReducer);
  const [bgColor, setBgColor] = useState('inherit');

  const focusedNodeRef = useRef<any>({});

  useEffect(() => {
    const targetId = getAllPatientRes?.clickBackPatientId;

    if (targetId && focusedNodeRef.current[targetId]) {
      focusedNodeRef.current[targetId].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      setBgColor('#6366f124');

      const colorTimer = setTimeout(() => {
        setBgColor('inherit');
      }, 4000);

      return () => clearTimeout(colorTimer);
    }
  }, [getAllPatientRes?.clickBackPatientId]);

  return (
    <Box>
      <Table
        stickyHeader
        sx={{ minWidth: 700 }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Patient Name</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Payer Type</TableCell>
            <TableCell>Medical Record No.</TableCell>
            <TableCell>Status</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((patient: any) => {
            const isHighlighted = patient.uuid === getAllPatientRes?.clickBackPatientId;
            return (
              <TableRow
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isHighlighted ? bgColor : 'none',
                  transition: 'background-color 0.3s ease',
                }}
                key={patient?.uuid}
                onClick={() => onRowClick(patient)}
                title={
                  !permissionData?.includes(READ_EPISODE_OF_CARE)
                    ? "You don't have permission to access EOC."
                    : ''
                }
                ref={(el) => (focusedNodeRef.current[patient?.uuid] = el)}
              >
                <TableCell>
                  <Stack
                    alignItems="center"
                    direction="row"
                    gap="1rem"
                  >
                    <Avatar
                      src={`data:image/png;base64,${patient?.profileImage}`}
                      sx={{
                        height: 42,
                        width: 42,
                      }}
                    >
                      {getInitials(patient?.lastName) + getInitials(patient?.firstName)}
                    </Avatar>
                    {patient?.lastName}
                    {','} {patient?.firstName}
                    {/* {patient?.middleName} */}
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    textTransform: 'capitalize',
                  }}
                >
                  {patient?.gender ? patient?.gender?.toLowerCase() : '-'}
                </TableCell>
                <TableCell>
                  {patient?.payerType
                    ? patient?.payerType
                        ?.replace(/_/g, ' ')
                        ?.replace(
                          /\w\S*/g,
                          (txt: string) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
                        )
                    : '-'}
                </TableCell>
                <TableCell>
                  {patient?.medicalRecordNumber ? patient?.medicalRecordNumber : '-'}
                </TableCell>
                <TableCell>
                  {patient?.active ? (
                    <Label
                      backgroundColor="#15B79E1F"
                      color="#107569"
                      text="ACTIVE"
                    />
                  ) : (
                    <Label
                      backgroundColor="#F790091F"
                      color="#B54708"
                      text="INACTIVE"
                    />
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onRowClick(patient)}
                >
                  <ArrowForwardIcon />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ListingTable;
